<template>
    <div id="lt-yantr">
        <div class="container-width">
            <div class="loan-pay-filter-graph">
                <div class="filter-block">
                    <div class="LoanPayment--wrapper">
                        <div class="LoanPayment--form-group">
                            <div class="LoanPayment--filter-inputs">
                                <form ref="form" @submit.prevent="calculationLoanPayment()">
                                    <ul class="LoanPayment--filter-block">
                                        <li>
                                            <label>{{ labelLoanAmount }}</label>
                                            <number-input
                                                v-model="LoanIntAmount"
                                                inputName="balanceAmount"
                                                maxlength="6"
                                                minvalue="0"
                                                :showSymbol="false"
                                                no-decimal="true">
                                                <template #leftimage>
                                                    <div class="input-group-addon">
                                                        <img src="../../assets/images/dollar-icon.svg" height="20" width="20" alt="dollar-icon" />
                                                    </div>
                                                </template>
                                            </number-input>
                                        </li>
                                        <li>
                                            <label>{{ labelInterestRate }}</label>
                                            <number-input
                                                v-model="InterestIntRate"
                                                precision="2"
                                                max-value="100"
                                                inputName="averageInterestRate"
                                                :showSymbol="false"
                                                maxlength="4"
                                                minvalue="0"
                                                @input="validateAverageInterestRate()">
                                                <template #rightimage>
                                                    <div class="input-group-addon">
                                                        <img src="../../assets/images/percentage-icon.svg" height="20" width="20" alt="percentage-icon" />
                                                    </div>
                                                </template>
                                            </number-input>
                                        </li>
                                        <li>
                                            <drop-down
                                                v-model="LoanTerm"
                                                inputName="loanTerm"
                                                :label="labelLoanTerm"
                                                :options="LoanTermOption"
                                                @input="validateAverageInterestRate(), validateInterestPrecision(), validateLoanAmountBalance()"
                                            />
                                        </li>
                                        <li class="LoanPayment--btn-wrapper">
                                            <button class="btn LoanPayment--calculate-btn" type="submit">{{ calculationButtonText }}</button>
                                        </li>
                                    </ul>
                                </form>
                            </div>
                        </div>
                        <div class="LoanPayment--result-wrapper">
                            <div class="LoanPayment--result-container">
                                <div class="LoanPayment--result-top">
                                    <div class="LoanPayment--result-description">
                                        <div v-if="MonthlyPayment" class="LoanPayment--result-payoff">
                                            <div class="LoanPayment--result-headline">Estimated Monthly Payment</div>
                                            <div class="LoanPayment--result-amount">${{ currencyFilter(MonthlyPayment) }}</div>
                                        </div>
                                        <div class="LoanPayment--result-payment">
                                            <button class="btn LoanPayment--result-payment-btn" type="submit" @click="redirectToUrl()">{{ DestinationButtonText }}</button>
                                        </div>
                                    </div>
                                </div>
                                <div class="LoanPayment--result-bottom">
                                    <div class="chart-parent">
                                        <vue-chart
                                            id="donutChart"
                                            type="doughnut"
                                            :data="chartData"
                                            :options="options"
                                            :width="chartWidth"
                                            :height="chartHeight"
                                        />
                                        <div class="donut-inner">
                                            <label>Total Repayment:</label>
                                            <h3>${{ currencyFilter(TotalRepayment) }}</h3>
                                        </div>
                                    </div>
                                    <div class="LoanPayment--result-breakdown">
                                        <h4 class="LoanPayment--result-title">Payment Breakdown</h4>
                                        <ul class="LoanPayment--result-list">
                                            <li>
                                                <label><span class="LoanPayment--result-green-bg"></span> Principal Amount</label>
                                                <span class="LoanPayment--result-value">${{ currencyFilter(PrincipalAmount) }}</span>
                                            </li>
                                            <li>
                                                <label><span class="LoanPayment--result-teal-bg"></span> Total Interest Paid</label>
                                                <span class="LoanPayment--result-value">${{ currencyFilter(InterestPaid) }}</span>
                                            </li>
                                            <span v-if="amortizationSchedule === 1" class="LoanPayment--result-url">Show Payment Schedule</span>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="clear-fix"></div>
                </div>
            </div>
            <div v-if="moPayGraphSection==1 || amortizationTableSection==1" class="heading-section">
                <h2 class="section-title">Your monthly payment broken down</h2>
                <p class="sub-heading">Use this chart to see how much principal and interest you are paying with each payment</p>
            </div>
            <div v-if="moPayGraphSection==1 || amortizationTableSection==1" class="graph-table-wrapper">
                <home-loan-mo-pay-graph
                    v-if="moPayGraphSection==1"
                    :loan-data="AmortizationBreakdown"
                />
                <home-loan-amortization-table
                    v-if="amortizationTableSection==1"
                    ref="amortizationTableRef"
                    :amortization-breakdown="AmortizationBreakdown"
                    :loan-amount="LoanIntAmount"
                />
            </div>
        </div>
    </div>
</template>

<script>
    import Accounting from 'accounting';
    import VueChart from 'vue-chart-js';
    import NumberInput from '~/components/number-input.vue';
    import HomeLoanMoPayGraph from '~/components/home-loan-mo-pay-graph-new.vue';
    import HomeLoanAmortizationTable from '~/components/home-loan-amortization-table-new.vue';
    import PaymentHelper from '~/helpers/payment';
    import BaseMixin from '~/mixin/base-mixin';
    import AmortizationBreakdownHelper from '~/helpers/amortization-breakdown';
    import ChartTooltip from '~/helpers/chart-tooltip';

    export default {
        props: {
            amortizationSchedule: {
                default: 1,
            },
            labelLoanAmount: {
                default: 'Loan Amount',
            },
            labelInterestRate: {
                default: 'Estimated Interest Rate',
            },
            labelLoanTerm: {
                default: 'Loan Term',
            },
            calculationButtonText: {
                default: 'Calculate',
            },
            ltLogo: {
                default: 0,
            },
            minLoanAmount: {
                default: 1000,
            },
            maxLoanAmount: {
                default: 35000,
            },
            loanAmount: {
                default: 10000,
            },
            minInterestRate: {
                default: 6,
            },
            maxInterestRate: {
                default: 35,
            },
            interestRate: {
                default: 8,
            },
            minLoanTerm: {
                default: 3,
            },
            maxLoanTerm: {
                default: 7,
            },
            loanTerm: {
                default: 5,
            },
            disclosureTokens: {
                default: '00101',
            },
            disclosureText: {
                default: 'Advertising Disclosures',
            },
            moPayGraphSection: {
                default: 1,
            },
            amortizationTableSection: {
                default: 1,
            },
            destinationLinkOrId: {
                default: 'wp-personal',
            },
            destinationButtonText: {
                default: 'View Free Offers',
            },
            loanAmountInterval: {
                default: 1000,
            },
            interestRateInterval: {
                default: 0.1,
            },
            loanTermInterval: {
                default: 1,
            },
        },
        data() {
            return {
                LoanTermOption: [
                    {
                        text: '3 years',
                        value: 3,
                    },
                    {
                        text: '4 years',
                        value: 4,
                    },
                    {
                        text: '5 years',
                        value: 5,
                    },
                    {
                        text: '6 years',
                        value: 6,
                    },
                    {
                        text: '7 years',
                        value: 7,
                    },
                ],
                DisclosureText: this.disclosureText,
                DisclosureTokens: this.disclosureTokens,
                LoanIntAmount: Accounting.format(this.loanAmount),
                InterestIntRate: typeof this.interestRate == 'undefined' ? 3.3 : Accounting.formatNumber(this.interestRate, { precision: 2 }),
                LoanTerm: this.loanTerm,
                chartData: {
                    labels: [],
                    datasets: [
                        {
                            data: [],
                            backgroundColor: [
                                '#078181',
                                '#08c177',
                            ],
                        },
                    ],
                },
                options: {
                    cutoutPercentage: 80,
                    percentageInnerCutout: 80,
                    segmentShowStroke: true,
                    segmentStrokeColor: '#fff',
                    segmentStrokeWidth: 5,
                    legend: {
                        display: false,
                    },
                    tooltips: {
                        enabled: false,
                        custom: ChartTooltip.customTooltips,
                    },
                },
                DestinationLinkOrId: this.destinationLinkOrId,
                DestinationButtonText: this.destinationButtonText,
                chartWidth: 204,
                chartHeight: 204,
                InterestPaid: null,
                MonthlyPayment: null,
                TotalRepayment: null,
                PrincipalAmount: null,
                AmortizationBreakdown: {},
            };
        },
        methods: {
            validateLoanAmountBalance() {
                this.LoanIntAmount = Accounting.format(this.LoanIntAmount);
            },
            validateInterestPrecision() {
                this.InterestIntRate = this.currencyFilter(this.InterestIntRate);
            },
            validateAverageInterestRate() {
                this.InterestIntRate = this.InterestIntRate > 100.00 ? 100.00 : this.InterestIntRate;
            },
            calculatePercentage(value, divider) {
                return Math.round(value / divider * 100);
            },
            calculationLoanPayment() {
                const balance = Accounting.unformat(this.LoanIntAmount);
                this.MonthlyPayment = PaymentHelper.monthlyPaymentUsingLoanTermInYears(balance, this.LoanTerm, this.InterestIntRate);
                this.InterestPaid = this.MonthlyPayment * (this.LoanTerm * 12) - balance;
                this.TotalRepayment = this.InterestPaid + balance;
                this.PrincipalAmount = balance;
                this.initializeChartData(balance, this.TotalRepayment);
                this.AmortizationBreakdown = AmortizationBreakdownHelper.amortizationBreakdown(balance, this.LoanTerm, this.InterestIntRate);
                this.LoanIntAmount = Accounting.format(this.LoanIntAmount);
            },
            initializeChartData(balance, totalRepayment) {
                this.chartData.datasets[0].data = [];
                this.chartData.labels = [];
                this.chartData.datasets[0].data.push(this.calculatePercentage(this.InterestPaid, totalRepayment));
                this.chartData.datasets[0].data.push(this.calculatePercentage(balance, totalRepayment));
                this.chartData.labels.push(`Total Interest Paid ${Accounting.formatMoney(this.InterestPaid, { precision: 0 })}`);
                this.chartData.labels.push(`Principal Amount ${Accounting.formatMoney(balance, { precision: 0 })}`);
            },
            currencyFilter(figure) {
                return figure > 0 ? Accounting.formatNumber(figure, { precision: 2 }) : 0;
            },
            redirectToUrl() {
                const amount = Accounting.unformat(this.LoanIntAmount);
                this.DestinationLinkOrId = `${this.destinationLinkOrId}&amount=${amount}`;
                this.redirect();
            },
        },
        mounted() {
            this.calculationLoanPayment();
            this.validateAverageInterestRate();
        },
        components: {
            NumberInput,
            HomeLoanMoPayGraph,
            HomeLoanAmortizationTable,
            VueChart,
        },
        mixins: [BaseMixin],
    };
</script>
<style src="../../assets/styles/common.scss" lang="scss"></style>
<!-- Below main.scss file is common for "Home Loan Payment & Home Loan Payment Lite & Loan Payment V2" Calculator. -->
<style src="../home-loan-payment/main.scss" lang="scss"></style>
<style src="./main.scss" lang="scss"></style>
